// LoginLayout.tsx
import { useState, useEffect } from "react";

import * as React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import styles from "src/pages/Login.module.css";
import { getLogo } from "src/services/logoService";
import { isSoneraHost } from "src/services/runtimeConfig";
import InstallBannerForIOS from "src/components/InstallBannerForIOS";

type LoginLayoutProps = {
  children: React.ReactNode;
  // "sonera-launch" forces the post-rebrand look (DNCScrub-by-Sonera lockup +
  // the Sonera gradient, same as dnc.sonera.dev) on ANY host, regardless of
  // domain. Used by the /login-on-7-13 preview so stakeholders can see exactly
  // what customers get once the Sonera name goes live on July 6.
  // "merger-notice" shows the DNCScrub-by-Sonera lockup + merger notice but
  // KEEPS the original theme colors (teal gradient, blue buttons). Used by the
  // /login-on-7-28 preview; goes live on /login via MERGER_LOCKUP_LIVE below.
  variant?: "sonera-launch" | "merger-notice";
};

// *** 7/28 MERGER LAUNCH SWITCH (launch moved from 7/23, then 7/30) ***
// Flip to true and deploy on 7/28 to put the "DNCScrub by Sonera" lockup and
// the merger notice on the regular /login page. Theme colors stay unchanged;
// whitelabelled accounts are unaffected. Preview anytime at /login-on-7-28.
const MERGER_LOCKUP_LIVE = true;

// On the Sonera brand domains (dnc.sonera.dev / dnc.sonera.co) the login page
// shows Sonera branding — the Sonera lockup + the Sonera gradient/colors.
const isSonera =
  typeof window !== "undefined" && isSoneraHost(window.location.hostname);

const LoginLayout: React.FC<LoginLayoutProps> = ({ children, variant }) => {
  // The launch preview turns on Sonera branding everywhere, but with the
  // "DNCScrub by Sonera" lockup instead of the dnc.sonera.dev Sonera wordmark.
  const isLaunchPreview = variant === "sonera-launch";
  const useSoneraBranding = isSonera || isLaunchPreview;
  // Teal-theme lockup mode: real lockup + merger notice, original colors.
  // Sonera hosts get the full branding instead, and whitelabelled accounts
  // keep their own logo (handled in the effect below).
  const useMergerLockup =
    !useSoneraBranding &&
    (MERGER_LOCKUP_LIVE || variant === "merger-notice");
  const [logo, setLogo] = useState("");
  const [isWhitelabelled, setIsWhitelabelled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const defaultLogo = "/images/DNCScrubLight.png";
    // Sonera hosts (dnc.sonera.co / dnc.sonera.dev) and the launch preview both
    // show the "DNCScrub by Sonera" lockup — the maroon DNCScrub wordmark over the
    // real Sonera mark + Sonera-font wordmark. The on-light variant reads cleanly
    // on the white login box (the navbar lockup's beige wordmark is invisible here).
    if (useSoneraBranding) {
      setLogo("/images/DNCScrub-by-Sonera-lockup-onlight.svg");
      return;
    }
    const fetchedLogo = getLogo("");
    if (fetchedLogo) {
      setIsWhitelabelled(true);
      setLogo(fetchedLogo);
    } else if (useMergerLockup) {
      // Merger-lockup mode swaps only the logo; theme colors stay as-is.
      setLogo("/images/DNCScrub-by-Sonera-lockup-onlight.svg");
    } else {
      setLogo(defaultLogo);
    }
  }, []);

  const supportEmail = useSoneraBranding
    ? "support@sonera.co"
    : logo.includes("nxxg")
      ? "compliance@nexxagroup.com"
      : "support@dnc.com";

  const customClass =
    location.pathname === "/login/self-register"
      ? styles.selfRegisterLoginFormContainer
      : "";

  return (
    <>
      <Helmet>
        <title>Login</title>
        <link
          href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css"
          rel="stylesheet"
        />
      </Helmet>
      {/* This div gives gradient background. On Sonera hosts the .sonera modifier
          swaps in the Sonera maroon→orange→beige gradient + orange buttons/links. */}
      <div
        className={`${styles["login-page-background"]} ${
          useSoneraBranding ? styles.sonera : ""
        }`}
      >
        {/* This div gives us a flexbox container so login box centered but still towards top of screen */}
        <div className={`${styles["login-form-container"]} ${customClass}`}>
          {/* This div is the actual login box */}
          <div className={styles["login-form"]}>
            <img
              src={logo}
              alt={
                useSoneraBranding || (useMergerLockup && !isWhitelabelled)
                  ? "DNCScrub by Sonera"
                  : "DNCScrub Logo"
              }
              className={styles.appLogo}
            />
            {/* Merger notice shown on the Sonera-branded login and in
                merger-lockup mode so customers understand the rebrand at sign-in. */}
            {(useSoneraBranding || (useMergerLockup && !isWhitelabelled)) && (
              <div className={styles["merger-notice"]}>
                <strong className={styles["merger-notice-title"]}>
                  Contact Center Compliance + Pure Caller ID have merged to form
                  Sonera
                </strong>
                <span>
                  Your account, login, and data are unchanged — sign in as usual.{" "}
                  <a
                    href="https://sonera.co/company/sonera-announcement/"
                    target="_blank"
                    rel="noreferrer noopener"
                    className={styles["merger-notice-link"]}
                  >
                    Learn more
                  </a>
                </span>
              </div>
            )}
            {children}
            <p className="mt-3 mb-0 flex align-items-center">
              Questions? E-mail{" "}
              <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
            </p>

            <InstallBannerForIOS />
          </div>
        </div>
      </div>
      {!isWhitelabelled && !useSoneraBranding && (
        <footer className={styles["login-page-footer"]}>
          <a target="_blank" href="https://dnc.com/" rel="noreferrer noopener">
            Contact Center Compliance, Corp.
          </a>{" "}
          {/* Questions? <a href="mailto:info@dnc.com">support@dnc.com</a> */}
        </footer>
      )}
    </>
  );
};

export default LoginLayout;

// Layout for the /login-on-7-13 preview: the exact login page customers will
// see once the Sonera rebrand is announced. WithLayout renders Layout with only
// `children`, so we bind the variant here.
export const SoneraLaunchLoginLayout: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <LoginLayout variant="sonera-launch">{children}</LoginLayout>
);

// Layout for the /login-on-7-28 preview: the 7/28 merger-day login — the
// "DNCScrub by Sonera" lockup + merger notice with the ORIGINAL theme colors.
// This is what /login itself shows once MERGER_LOCKUP_LIVE is flipped.
export const MergerNoticeLoginLayout: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <LoginLayout variant="merger-notice">{children}</LoginLayout>
);
